
import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { OrderStatus } from '@/types/orders';

interface CartConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  orderStatus: OrderStatus;
  onRequestPayment?: () => void;
  onSendReminder?: () => void;
  canSendReminder?: boolean;
}

export function CartConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  orderStatus,
  onRequestPayment,
  onSendReminder,
  canSendReminder = true,
}: CartConfirmationDialogProps) {
  const { t } = useTranslation();
  
  const getStatusDisplay = (status: OrderStatus) => {
    return t(`orderStatus.${status}`);
  };
  
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('confirmAddToExistingOrder')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('youHaveAnActiveOrderWithStatus')} {getStatusDisplay(orderStatus)}. 
            {t('doYouWantToAddMoreItemsToThisOrder')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        {(onRequestPayment || onSendReminder) && (
          <div className="flex flex-col gap-3 my-4">
            {onRequestPayment && (
              <Button 
                variant="outline" 
                onClick={() => {
                  onRequestPayment();
                  onOpenChange(false);
                }}
              >
                {t('requestPayment')}
              </Button>
            )}
            
            {onSendReminder && (
              <Button 
                variant="outline" 
                onClick={() => {
                  onSendReminder();
                  onOpenChange(false);
                }}
                disabled={!canSendReminder}
              >
                {t('orderTakingTooLong')}
                {!canSendReminder && (
                  <span className="text-xs ml-2">{t('reminderRecentlySent')}</span>
                )}
              </Button>
            )}
          </div>
        )}
        
        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{t('confirmAddItems')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
